import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Divider,
  Typography,
  CircularProgress,
  Autocomplete,
  IconButton
} from '@mui/material';
import { Save, Trash, CirclePlus } from 'lucide-react';
import { toast } from 'react-toastify';
import { FilterFieldSx, DropDownSX, CSS_PRIMARY } from '../../../../constants/style';
import { paramsPrefix } from '../../../../constants/constant';
import { deleteArea, saveArea } from '../../../../apis/Masters/Area';
import { deleteDcrArea, saveDcrArea } from '../../../../apis/DCR/Area';
import { fetchCityListCombo } from '../../../../apis/Masters/City';
import { fetchDcrCityListCombo } from '../../../../apis/DCR/City';
import ConfirmDialog from '../../common/ConfirmDialog';
import { userScreenAccess } from '../../../../utils';
import Tooltip from '../../../../utils/Tooltip';
import CommonDialog from '../../common/CommonDialog';
import { isDcrScreen } from '../../../../utils';
import AddCity from '../City/AddCity';

/* ── Empty form state ─────────────────────────────────────────────────────── */
const emptyForm = {
  area: '',
  isActive: true,
  areaGUID: 0,
  cityGUID: 0
};

/* ── Validation ───────────────────────────────────────────────────────────── */
const validate = (form) => {
  const errors = {};
  if (!form.area.trim()) {
    errors.area = 'Area Name is required.';
  }
  if (!form.cityGUID) {
    errors.cityGUID = 'City selection is required.';
  }
  return errors;
};

const AddArea = ({ isDcrArea = false, areaData = null, onRefresh, onClose, userAccess }) => {
  const isEditMode = Boolean(areaData);

  const [form, setForm] = useState(emptyForm);
  const [cities, setCities] = useState([]);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [openAddCityDialog, setOpenAddCityDialog] = useState(false);
  const isDeleteAllowd = isEditMode ? userAccess.hasDeletePermission : false;
  const isEditAllowed = userAccess.hasEditPermission || userAccess.hasAddPermission;
  const isCityPermission = userScreenAccess('/city');
  const isDcrCity = isDcrScreen();

  // ── Confirm-dialog state ────────────────────────────────────────────────
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    content: '',
    onConfirm: null
  });

  const openConfirm = ({ title, content, onConfirm }) =>
    setConfirmDialog({ open: true, title, content, onConfirm });

  const closeConfirm = () =>
    setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));

  const deleteHandler = () => {
    if (!userAccess?.hasDeletePermission) return;
    openConfirm({
      title: 'Delete Area Record',
      content: `Are you sure you want to delete the Area record for "${areaData.area || 'this area'
        }"? This action cannot be undone.`,
      onConfirm: () => {
        closeConfirm();
        handleDelete();
      }
    });
  };

  // Fetch Cities for Dropdown
  useEffect(() => {
    fetchDependencies();
  }, []);

  useEffect(() => {
    if (areaData) {
      if (isDcrArea) {
        setForm({
          area: areaData.dailyCallArea || '',
          isActive: areaData.isActive,
          cityGUID: areaData.dailyCallCityGUID || 0,
          areaGUID: areaData.dailyCallAreaGUID || 0
        });
      } else {
        setForm({
          area: areaData.area || '',
          isActive: areaData.isActive,
          cityGUID: areaData.cityGUID || 0,
          areaGUID: areaData.areaGUID || 0
        });
      }
    } else {
      setForm(emptyForm);
    }
    setErrors({});
    setTouched({});
  }, [areaData]);

  // ── Field change ───────────────────────────────────────────────────────────
  const handleChange = (field) => (e) => {
    const value =
      e.target.type === 'checkbox' ? e.target.checked : (e.target.value || '');
    const next = { ...form, [field]: value };
    setForm(next);

    // Live-validate touched fields
    if (touched[field]) {
      const errs = validate(next);
      setErrors((prev) => ({ ...prev, [field]: errs[field] }));
    }
  };

  // ── Autocomplete change handler ─────────────────────────────────────────────
  const handleCityChange = (event, value) => {
    const cityGUID = value?.cityGUID || value?.dailyCallCityGUID || 0;
    const next = { ...form, cityGUID };
    setForm(next);

    // Live-validate touched fields
    if (touched.cityGUID) {
      const errs = validate(next);
      setErrors((prev) => ({ ...prev, cityGUID: errs.cityGUID }));
    }
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const errs = validate(form);
    setErrors((prev) => ({ ...prev, [field]: errs[field] }));
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    // Touch all fields to reveal errors
    const allTouched = Object.fromEntries(Object.keys(form).map((k) => [k, true]));
    setTouched(allTouched);

    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    try {
      const payload = {
        area: (form.area.trim()).toUpperCase(),
        [paramsPrefix + 'CityGUID']: form.cityGUID,
        isActive: form.isActive,
        [paramsPrefix + 'AreaGUID']: form.areaGUID,
        sortNo: 0
      };

      const dcrPayload = {
        dailyCallArea: form.area.trim(),
        dailyCallCityGUID: form.cityGUID,
        isActive: form.isActive,
        dailyCallAreaGUID: form.areaGUID,
        dailyCallCity: areaData?.dailyCallCity || '',
        sortNo: 0
      };

      const response = isDcrArea ? await saveDcrArea(dcrPayload) : await saveArea(payload);
      // Depending on API response structure, adjust logic below:
      if (response && response.success) {
        onClose?.();
        onRefresh?.();
      }
    } catch (err) {
      console.log('Failed to save area:', err);
      toast.error(err.response?.data?.message || 'Failed to save area. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const fetchDependencies = async () => {
    try {
      if (isDcrArea) {
        const [citys] = await Promise.all([fetchDcrCityListCombo()]);

        const loadedCitys = citys || [];

        setCities(loadedCitys);
      } else {
        const [citys] = await Promise.all([fetchCityListCombo()]);

        const loadedCitys = citys || [];

        setCities(loadedCitys);
      }
    } catch (err) {
      console.log('Failed to fetch dependencies:', err);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const successMsg = `Area deleted successfully`;
      const response = isDcrArea
        ? await deleteDcrArea(areaData?.dailyCallAreaGUID, successMsg, '')
        : await deleteArea(areaData?.areaGUID, successMsg, '');
      if (response && response.success) {
        onClose?.();
        onRefresh?.();
      }
    } catch (err) {
      console.log('Failed to delete global type:', err);
    } finally {
      setDeleting(false);
    }
  };

  // ── Has any visible error ──────────────────────────────────────────────────
  const hasErrors = Object.keys(validate(form)).length > 0;

  return (
    <>
      <Box component="form" noValidate autoComplete="off">
        <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 2.5 }}>
          {/* Area Name */}
          <Box sx={{ flex: '1 1 50%', minWidth: '200px' }}>
            <TextField
              fullWidth
              label="Area Name"
              placeholder="e.g. Downtown"
              value={form.area}
              onChange={handleChange('area')}
              onBlur={handleBlur('area')}
              error={touched.area && Boolean(errors.area)}
              helperText={touched.area && errors.area}
              size="small"
              sx={{
                ...FilterFieldSx,
                '& input': {
                  textTransform: 'uppercase'
                }
              }}
              slotProps={{
                input: {
                  readOnly: !isEditAllowed
                }
              }}
            />
          </Box>

          {/* City Dropdown */}
          <Box
            sx={{
              flex: '1 1 50%',
              minWidth: '200px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1
            }}
          >
            <Autocomplete
              size="small"
              fullWidth
              options={cities}
              readOnly={!isEditAllowed}
              value={
                cities.find(
                  (c) => c.cityGUID === form.cityGUID || c.dailyCallCityGUID === form.cityGUID
                ) || null
              }
              onChange={handleCityChange}
              onBlur={handleBlur('cityGUID')}
              disablePortal={false}
              getOptionLabel={(option) => option?.city || option?.dailyCallCity || ''}
              isOptionEqualToValue={(option, value) =>
                option?.cityGUID === value?.cityGUID ||
                option?.dailyCallCityGUID === value?.cityGUID
              }
              slotProps={{
                paper: {
                  sx: DropDownSX
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="City"
                  placeholder="Select City"
                  error={touched.cityGUID && Boolean(errors.cityGUID)}
                  helperText={touched.cityGUID && errors.cityGUID}
                  sx={FilterFieldSx}
                />
              )}
              sx={{ flex: 1 }}
            />

            {isCityPermission?.hasAddPermission && (
              <Tooltip title="Add New City">
                <IconButton
                  size="small"
                  onClick={() => setOpenAddCityDialog(true)}
                  sx={{
                    mt: '4px'
                  }}
                >
                  <CirclePlus size={18} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* isActive */}
          <Box sx={{ flex: '1 1 50%', minWidth: '200px', display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.isActive}
                  onChange={handleChange('isActive')}
                  size="small"
                  sx={{
                    color: 'primary.main',
                    '&.Mui-checked': { color: CSS_PRIMARY }
                  }}
                  readOnly={!isEditAllowed}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Active
                </Typography>
              }
            />
          </Box>
        </Box>

        {/* ── Actions ─────────────────────────────────────────────────────── */}
        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={
              deleting ? <CircularProgress size={14} color="inherit" /> : <Trash size={15} />
            }
            disabled={deleting}
            onClick={deleteHandler}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 100,
              background: '#e05757',
              '&:hover': { background: '#6d6464' },
              boxShadow: '0 4px 12px rgba(46,204,113,0.3)',
              display: !isEditMode || !isDeleteAllowd ? 'none' : 'inline-flex'
            }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>

          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save size={15} />}
            disabled={saving || (hasErrors && Object.keys(touched).length > 0)}
            onClick={handleSave}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 100,
              background: 'var(--primary, #36c95f)',
              '&:hover': { background: 'var(--primary-dark, #27ae60)' },
              boxShadow: '0 4px 12px rgba(46,204,113,0.3)',
              display: isEditAllowed ? 'inline-flex' : 'none'
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Box>

      <CommonDialog
        open={openAddCityDialog}
        onClose={() => {
          setOpenAddCityDialog(false);
        }}
        title={'Add New City'}
        maxWidth="sm"
        persistent={true}
      >
        <AddCity
          isDcrCity={isDcrCity}
          cityData={null}
          userAccess={isCityPermission}
          onRefresh={fetchDependencies}
          onClose={() => {
            setOpenAddCityDialog(false);
          }}
        />
      </CommonDialog>

      {/* ── Confirm Dialog ─────────────────────────────────────────────── */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={closeConfirm}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        content={confirmDialog.content}
        titleIcon={<Trash size={18} />}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />
    </>
  );
};

export default AddArea;
